/**
 * Sunucudan gelen hatayı kullanıcıya gösterilecek Türkçe metne çevirir.
 *
 * NİÇİN TEK YER: eski sistemde `post.php` her durumda HTTP 200 ve "başarıyla
 * güncellendi" dönüyordu; istemcinin `.fail()` bloğu hiç tetiklenmiyor, kullanıcı
 * kaydın gittiğini sanıyordu. Yeni sistemde Cloud Function gerçek hata kodu ve
 * Türkçe mesaj döndürüyor — bu fonksiyonun tek işi o mesajı KAYBETMEMEK.
 *
 * Asla "Bir hata oluştu" gibi içi boş bir metne düşmez; sunucunun söylediğini
 * söyler. Sunucu hiçbir şey söylemediyse bunu açıkça belirtir.
 */

/** Firebase callable hatası — `code` ve `details` taşır. */
interface CallableError {
  code?: string;
  message?: string;
  details?: unknown;
}

/** Kullanıcının anlamadığı ham Firebase kodlarını insanca karşılıklarına çevirir. */
const CODE_TEXT: Record<string, string> = {
  "functions/unauthenticated": "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.",
  "functions/permission-denied": "Bu işlem için yetkiniz yok.",
  "functions/not-found": "Kayıt bulunamadı.",
  "functions/invalid-argument": "Girilen bilgilerde hata var.",
  "functions/failed-precondition": "İşlem şu anki durumda yapılamıyor.",
  "functions/aborted": "İşlem çakışma nedeniyle iptal edildi. Lütfen tekrar deneyin.",
  "functions/unavailable": "Servise şu anda ulaşılamıyor. Lütfen tekrar deneyin.",
  "permission-denied": "Bu kaydı görme veya değiştirme yetkiniz yok.",
  "unavailable": "Bağlantı kurulamadı. İnternet bağlantınızı kontrol edin.",
};

/**
 * @param fallback Sunucu anlamlı bir şey söylemediğinde gösterilecek, bağlama
 *   özgü metin (ör. "SSH kaydı açılamadı."). Sunucunun kendi mesajını ASLA
 *   ezmez — yalnızca boşluğu doldurur.
 */
export function errorText(e: unknown, fallback?: string): string {
  if (e === null || e === undefined) {
    return fallback ?? "Bilinmeyen bir hata oluştu.";
  }
  if (typeof e === "string") return e.trim() || (fallback ?? "Bilinmeyen bir hata oluştu.");

  const err = e as CallableError;

  // Sunucunun kendi Türkçe mesajı VARSA her zaman o kazanır.
  const msg = typeof err.message === "string" ? err.message.trim() : "";
  if (msg && !isRawFirebaseMessage(msg)) return msg;

  if (err.code && CODE_TEXT[err.code]) return CODE_TEXT[err.code] as string;
  if (fallback) return fallback;
  if (msg) return msg;

  return "Sunucu bir hata döndürdü ama açıklama vermedi.";
}

/** Firebase'in İngilizce şablon mesajlarını (kullanıcıya anlamsız) ayıklar. */
function isRawFirebaseMessage(m: string): boolean {
  return /^(INTERNAL|internal|Missing or insufficient permissions|FirebaseError)/.test(m);
}

/**
 * Girdi doğrulama hatalarını alan bazında döker.
 * Cloud Function `zod` sorunlarını `details.issues` içinde gönderiyor.
 */
export function fieldIssues(e: unknown): Array<{ path: string; message: string }> {
  const details = (e as CallableError)?.details as
    | { issues?: Array<{ path?: string; message?: string }> }
    | undefined;
  if (!details?.issues) return [];
  return details.issues.map((i) => ({
    path: String(i.path ?? ""),
    message: String(i.message ?? ""),
  }));
}
